import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/utils/axios";
import avatarFallback from "@/assets/UI/avatar.png";
import s from "./Wallets.module.scss";
import walletIcon from "@/assets/UI/wallet.webp";
import coinIcon from "@/assets/UI/coin.webp";
import { formatDateTime } from "@/utils/formatDateTime";

const DEFAULT_LIMIT = 50;

const Wallets = () => {
  const navigate = useNavigate();
  const [wallets, setWallets] = useState([]);
  const [summary, setSummary] = useState({
    totalBalanceNoty: 0,
    walletCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({ username: "" });

  const fetchWallets = async (nextFilters = filters) => {
    try {
      setLoading(true);
      setError("");
      const res = await api.get("/v1/admin/finance/wallets", {
        params: {
          limit: DEFAULT_LIMIT,
          username: nextFilters.username || undefined,
        },
      });
      setWallets(res.data?.data ?? []);
      setSummary(res.data?.summary ?? { totalBalanceNoty: 0, walletCount: 0 });
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load wallets",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFilterChange = (field) => (event) => {
    setFilters((prev) => ({ ...prev, [field]: event.target.value }));
  };

  const applyFilters = (event) => {
    event.preventDefault();
    fetchWallets();
  };

  const resetFilters = () => {
    const nextFilters = { username: "" };
    setFilters(nextFilters);
    fetchWallets(nextFilters);
  };

  const stats = useMemo(() => {
    const walletCount = summary.walletCount ?? 0;
    const totalBalanceNoty = summary.totalBalanceNoty ?? 0;
    return { walletCount, totalBalanceNoty };
  }, [summary]);

  return (
    <div className={s.page}>
      <header className={s.header}>
        <div className={s.headerLeft}>
          <p className={s.eyebrow}>Finance • Wallets</p>
          <h1 className={s.title}>Member wallets</h1>
          <p className={s.subtitle}>
            Review member balances and jump into wallet details.
          </p>
        </div>
      </header>

      <section className={s.statsGrid}>
        <div className={s.statCard}>
          <div className={s.statTop}>
            <p className={s.muted}>Total wallets</p>
          </div>
          <p className={s.metric}>{stats.walletCount}</p>
        </div>

        <div className={s.statCard}>
          <div className={s.statTop}>
            <p className={s.muted}>Total Noty balance</p>
          </div>
          <p className={s.metric}>
            <span className={s.metricCoin}>
              <img className={s.coinIcon} src={coinIcon} alt="" />
              <span>{stats.totalBalanceNoty}</span>
            </span>
            <span className={s.metricUnit}>NOTY</span>
          </p>
        </div>
      </section>

      <section className={s.card}>
        <div className={s.sectionHead}>
          <h2 className={s.sectionTitle}>Search</h2>
          <p className={s.sectionHint}>Filter by username and hit apply.</p>
        </div>

        <form className={s.filters} onSubmit={applyFilters}>
          <label className={s.formRow}>
            <span className={s.label}>Username</span>
            <div className={s.inputWrap}>
              <input
                className={s.input}
                value={filters.username}
                onChange={handleFilterChange("username")}
                placeholder="Search by username"
              />
              {filters.username ? (
                <button
                  type="button"
                  className={s.clearBtn}
                  onClick={() => setFilters((p) => ({ ...p, username: "" }))}
                  aria-label="Clear username"
                  title="Clear"
                >
                  ✕
                </button>
              ) : null}
            </div>
          </label>

          <div className={s.inlineActions}>
            <button className={`${s.btn} ${s.btnPrimary}`} type="submit">
              Apply
            </button>
            <button
              className={`${s.btn} ${s.btnGhost}`}
              type="button"
              onClick={resetFilters}
            >
              Reset
            </button>
          </div>
        </form>
      </section>

      {error && (
        <div className={`${s.stateBox} ${s.stateBoxError}`}>
          <strong>Something went wrong</strong>
          <span>{error}</span>
        </div>
      )}

      <section className={s.card}>
        <div className={s.sectionHead}>
          <h2 className={s.sectionTitle}>Wallet list</h2>
          <p className={s.sectionHint}>
            Click a member to open profile, or view wallet details.
          </p>
        </div>

        {loading ? (
          <div className={s.stateBox}>Loading wallets…</div>
        ) : wallets.length === 0 ? (
          <div className={s.stateBox}>No wallets found.</div>
        ) : (
          <div className={s.tableWrap}>
            <table className={s.table}>
              <thead>
                <tr>
                  <th>User</th>
                  <th>Balance (Noty)</th>
                  <th>Updated</th>
                  <th className={s.thActions}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {wallets.map((wallet) => {
                  const status = String(
                    wallet.status || "active",
                  ).toLowerCase();
                  return (
                    <tr key={wallet.walletId || wallet.id}>
                      <td>
                        <Link
                          to={`/admin/members/single/${wallet.userId}`}
                          className={s.userCell}
                        >
                          <span className={s.avatarRing}>
                            <img
                              src={wallet.avatar?.url || avatarFallback}
                              alt=""
                              className={s.avatar}
                              loading="lazy"
                            />
                          </span>

                          <span className={s.userMeta}>
                            <span className={s.userNameRow}>
                              <span className={s.userName}>
                                {wallet.user?.username || "Unknown user"}
                              </span>
                              <span
                                className={`${s.statusBadge} ${s[`status_${status}`] || ""}`}
                                title={wallet.status}
                              >
                                {wallet.status}
                              </span>
                            </span>
                            <span className={s.userSub}>
                              Tap to view profile
                            </span>
                          </span>
                        </Link>
                      </td>

                      <td>
                        <div className={s.balanceCell}>
                          <span className={s.balanceValue}>
                            {wallet.balanceNoty ?? 0}
                          </span>
                          <img className={s.coinIconSm} src={coinIcon} alt="" />
                        </div>
                      </td>

                      <td className={s.dateCell}>
                        {(() => {
                          const dt = formatDateTime(wallet.updatedAt);
                          if (!dt) return "-";

                          return (
                            <div className={s.dateTime}>
                              <div className={s.date}>
                                {dt.day}
                                <sup className={s.sup}>{dt.suffix}</sup>{" "}
                                {dt.month} {dt.year}
                              </div>
                              <div className={s.time}>{dt.time}</div>
                            </div>
                          );
                        })()}
                      </td>

                      <td>
                        <div className={s.rowActions}>
                          <button
                            type="button"
                            className={`${s.btn} ${s.btnWallet}`}
                            onClick={() =>
                              navigate(
                                `/admin/finance/wallets/${wallet.walletId}`,
                              )
                            }
                            title="View wallet"
                          >
                            <img
                              className={s.btnIcon}
                              src={walletIcon}
                              alt=""
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default Wallets;
